import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert, ScrollView } from 'react-native';
import { AccountSetupView } from './AccountSetupView';
import { appConfig } from '../../config/configManager';

interface InitialSetupViewProps {
  onSetupComplete: (setupType: 'new-account' | 'existing-account' | 'guest-mode', credentials?: { email: string; password: string }) => void;
  onClose?: () => void;
  storage?: any;
  apiService?: any;
}

export const InitialSetupView: React.FC<InitialSetupViewProps> = ({ 
  onSetupComplete, 
  onClose, 
  storage, 
  apiService 
}) => {
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  const [chosenAccountType, setChosenAccountType] = useState<'new-account' | 'existing-account' | null>(null);

  const handleSetupChoice = (choice: 'new-account' | 'existing-account' | 'guest-mode') => {
    if (choice === 'guest-mode') {
      onSetupComplete('guest-mode');
      return;
    }
    
    // Check if storage and apiService are available
    if (!storage || !apiService) {
      Alert.alert(
        'Service Unavailable',
        'Account features require storage and API services. Please try again or continue without an account.',
        [
          { text: 'Continue as Guest', onPress: () => onSetupComplete('guest-mode') },
          { text: 'Try Again', style: 'cancel' }
        ]
      );
      return;
    }
    
    // Store the chosen account type and show AccountSetupView
    setChosenAccountType(choice);
    setShowAccountSetup(true);
  };

  const handleAccountSetupComplete = () => {
    // When account setup is completed from initial setup, navigate to main app
    console.log('✅ Account setup completed from initial setup, navigating to main app');
    onSetupComplete('new-account'); // This will hide initial setup and go to main app
  };

  const handleBackFromAccount = () => {
    setShowAccountSetup(false);
    setChosenAccountType(null);
  };

  // If showing account setup, render that view instead
  if (showAccountSetup && chosenAccountType) {
    return (
      <AccountSetupView 
        onBack={handleBackFromAccount}
        storage={storage}
        apiService={apiService}
        initialStep={chosenAccountType}
        isInitialSetup={true}
        onAccountSetupComplete={handleAccountSetupComplete}
      />
    );
  }
  // Render the welcome screen
  return (
    <View style={styles.container}>
      <View style={styles.backgroundImageContainer}>
        <View style={styles.backgroundOverlay} />
      </View>
      <ScrollView
        style={styles.welcomeScroll}
        contentContainerStyle={styles.welcomeContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={{ maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%', alignSelf: 'center' }}>
        <View style={styles.imageSection}>
          <View style={styles.heroIconCircle}>
            <Text style={styles.heroIconText}>🧒</Text>
          </View>
        </View>
        
        <Text style={styles.welcomeTitle}>Welcome to Child Nav!</Text>
        <Text style={styles.welcomeSubtitle}>
          Empowering families to navigate their child's best possible health and development trajectory
        </Text>
        
        <View style={styles.optionsContainer}>
          <TouchableOpacity 
            style={styles.optionButton}
            onPress={() => handleSetupChoice('new-account')}
          >
            <Text style={styles.optionTitle}>Create New Family Account</Text>
            <Text style={styles.optionDescription}>
              Set up a new account to sync data and connect with other family members, providers and services
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.optionButton}
            onPress={() => handleSetupChoice('existing-account')}
          >
            <Text style={styles.optionTitle}>Sign Into Existing Family Account</Text>
            <Text style={styles.optionDescription}>
              Already have an account or account access has been setup for you? Sign in here
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.optionButton, styles.guestOptionButton]}
            onPress={() => handleSetupChoice('guest-mode')}
          >
            <Text style={styles.optionTitle}>Continue Without Account</Text>
            <Text style={styles.optionDescription}>
              Use the app locally without syncing (data stays on this device)
            </Text>
          </TouchableOpacity>
        </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1e293b',
  },
  backgroundImageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#3b82f6',
  },
  backgroundOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
  },
  welcomeScroll: {
    flex: 1,
    zIndex: 1,
  },
  welcomeContent: {
    flexGrow: 1,
    padding: 24,
    justifyContent: 'center',
  },
  imageSection: {
    alignItems: 'center',
    marginBottom: 32,
  },
  heroIconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    borderWidth: 1,
    borderColor: '#93c5fd',
  },
  heroIconText: {
    fontSize: 42,
    lineHeight: 48,
  },
  welcomeTitle: {
    fontSize: 34,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 12,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  welcomeSubtitle: {
    fontSize: 18,
    color: '#e2e8f0',
    textAlign: 'center',
    marginBottom: 40,
    lineHeight: 26,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  optionsContainer: {
    gap: 16,
  },
  optionButton: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
  },
  guestOptionButton: {
    borderColor: '#d1d5db',
    backgroundColor: '#f9fafb',
  },
  optionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 8,
  },
  optionDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
});